'use client';

/**
 * RagContent — registry renderer for a standalone browse_aol_content
 * result ({ data: RAGData }, resolved via the tool registry).
 *
 * In the composed flow, RAG turns render through RagAnswerBlock (page.tsx
 * groups the turn's refs + answer); this renderer is the registry-contract
 * fallback for a RAG result that surfaces outside that grouping. It
 * delegates to the same v3 media components so both paths stay identical:
 * one reference → MediaHeroCard, several → RelatedMediaPanel.
 */

import React, { memo, useMemo } from 'react';
import type { RAGData } from '@/types/rag';
import { cn } from '@/lib/utils';
import { pickMediaComponent } from './pick-media';

interface RagContentProps {
  data: RAGData;
  className?: string;
}

export const RagContent = memo(function RagContent({
  data,
  className,
}: RagContentProps) {
  const sorted = useMemo(() => {
    const refs = data.references?.references ?? [];
    return [...refs].sort((a, b) => (b.score ?? 0) - (a.score ?? 0));
  }, [data]);

  if (sorted.length === 0) return null;

  return (
    // Entrance animation deliberately NOT applied here: this renderer mounts
    // inside MessageBlock's ToolResultSlot, which owns the arrival for every
    // tool card — wrapping again would double-animate.
    <div className={cn('w-full', className)}>{pickMediaComponent(sorted)}</div>
  );
});

export default RagContent;
